<mat-card class="ai-feedback-container">
  <mat-card-title fxLayout="row" fxLayoutAlign="space-between center">
    <h3 class="page-header">AI Feedback Dashboard</h3>
  </mat-card-title>

  <mat-card-content>
    <!-- Summary Cards -->
    <div class="summary-section" *ngIf="summary">
      <div class="summary-card total">
        <div class="summary-value">{{ summary.totalFeedbackEntries || 0 }}</div>
        <div class="summary-label">Total Feedback</div>
      </div>
      <div class="summary-card positive">
        <div class="summary-value">
          <i class="fa fa-thumbs-up"></i> {{ summary.thumbsUp || 0 }}
        </div>
        <div class="summary-label">Thumbs Up</div>
      </div>
      <div class="summary-card negative">
        <div class="summary-value">
          <i class="fa fa-thumbs-down"></i> {{ summary.thumbsDown || 0 }}
        </div>
        <div class="summary-label">Thumbs Down</div>
      </div>
      <div class="summary-card documents">
        <div class="summary-value">{{ summary.totalDocuments || 0 }}</div>
        <div class="summary-label">Documents with Feedback</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <mat-form-field appearance="outline">
        <mat-label>Feature Type</mat-label>
        <mat-select [(ngModel)]="selectedFeature" placeholder="All Features">
          <mat-option value="">All Features</mat-option>
          <mat-option *ngFor="let feature of featureOptions" [value]="feature">
            {{ getFeatureLabel(feature) }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Reaction</mat-label>
        <mat-select [(ngModel)]="selectedReaction" placeholder="All Reactions">
          <mat-option value="">All Reactions</mat-option>
          <mat-option value="ThumbsUp">
            <i class="fa fa-thumbs-up"></i> Thumbs Up
          </mat-option>
          <mat-option value="ThumbsDown">
            <i class="fa fa-thumbs-down"></i> Thumbs Down
          </mat-option>
        </mat-select>
      </mat-form-field>

      <button mat-raised-button color="primary" (click)="applyFilters()">
        <i class="fa fa-search"></i> Apply Filters
      </button>
      <button mat-button (click)="clearFilters()">
        <i class="fa fa-times"></i> Clear
      </button>
    </div>

    <!-- Error Message -->
    <div class="error-message" *ngIf="errorMessage">
      <mat-icon>error</mat-icon>
      {{ errorMessage }}
    </div>

    <!-- Loading Indicator -->
    <div class="loading-container" *ngIf="isLoading">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Loading feedback...</span>
    </div>

    <!-- Feedback List -->
    <div class="feedback-list" *ngIf="!isLoading && feedbackList.length > 0">
      <div class="feedback-item" *ngFor="let item of feedbackList">
        <div class="feedback-header" (click)="toggleRowExpanded(item.documentId)">
          <div class="reaction-indicator" [ngClass]="getReactionClass(item.feedback.reaction)">
            <i class="fa" [ngClass]="getReactionIcon(item.feedback.reaction)"></i>
          </div>
          <div class="feedback-main">
            <div class="comment-text">{{ truncateText(item.answerText, 150) }}</div>
            <div class="feedback-meta">
              <span class="feature-badge">{{ getFeatureLabel(item.feedback.feature) }}</span>
              <span class="user-email">{{ item.feedback.userEmail }}</span>
              <span class="feedback-date">{{ item.feedback.feedbackAt | date: 'MMM d, y, h:mm a' }}</span>
            </div>
          </div>
          <div class="expand-icon">
            <i class="fa" [ngClass]="isRowExpanded(item.documentId) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>
        </div>

        <!-- Expanded Content -->
        <div class="feedback-details" *ngIf="isRowExpanded(item.documentId)">
          <div class="detail-section">
            <h4>Original Comment</h4>
            <p class="full-comment">{{ item.answerText }}</p>
            <div class="question-info" *ngIf="item.question">
              <strong>Question:</strong> {{ item.question }}
            </div>
          </div>

          <div class="detail-section" *ngIf="item.feedback.comment">
            <h4>Feedback Comment</h4>
            <p class="feedback-comment">{{ item.feedback.comment }}</p>
          </div>

          <div class="detail-section" *ngIf="item.py_legal_analysis?.overall_assessment">
            <h4>Legal Analysis Summary</h4>
            <div class="legal-summary">
              <div class="summary-row">
                <span class="label">Urgency:</span>
                <span class="urgency-badge" [ngClass]="getUrgencyClass(item.py_legal_analysis.overall_assessment.urgency)">
                  {{ item.py_legal_analysis.overall_assessment.urgency | titlecase }}
                </span>
              </div>
              <div class="summary-row" *ngIf="item.py_legal_analysis.overall_assessment.summary">
                <span class="label">Summary:</span>
                <span>{{ item.py_legal_analysis.overall_assessment.summary }}</span>
              </div>
              <div class="summary-row">
                <span class="label">Legal Review Required:</span>
                <span [ngClass]="item.py_legal_analysis.overall_assessment.requires_legal_review ? 'text-danger' : 'text-success'">
                  {{ item.py_legal_analysis.overall_assessment.requires_legal_review ? 'Yes' : 'No' }}
                </span>
              </div>
              <div class="summary-row" *ngIf="item.py_legal_analysis.violations?.length > 0">
                <span class="label">Potential Violations:</span>
                <span class="text-warning">{{ item.py_legal_analysis.violations.length }}</span>
              </div>
            </div>
          </div>

          <div class="detail-section metadata">
            <div class="meta-item">
              <span class="label">Document ID:</span>
              <span>{{ item.documentId }}</span>
            </div>
            <div class="meta-item" *ngIf="item.surveyId">
              <span class="label">Survey ID:</span>
              <span>{{ item.surveyId }}</span>
            </div>
            <div class="meta-item" *ngIf="item.commentCreated">
              <span class="label">Comment Created:</span>
              <span>{{ item.commentCreated | date: 'MMM d, y, h:mm a' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div class="no-results" *ngIf="!isLoading && feedbackList.length === 0">
      <i class="fa fa-inbox"></i>
      <p>No AI feedback found.</p>
      <p class="hint">Try adjusting your filters or check back later.</p>
    </div>

    <!-- Pagination -->
    <div class="pagination-controls" *ngIf="!isLoading && totalRecords > 0">
      <button mat-button (click)="prevPage()" [disabled]="currentPage === 1">
        <i class="fa fa-chevron-left"></i> Previous
      </button>
      <span class="page-info">Page {{ currentPage }} of {{ totalPages }} ({{ totalRecords }} total)</span>
      <button mat-button (click)="nextPage()" [disabled]="currentPage >= totalPages">
        Next <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  </mat-card-content>
</mat-card>
